import { useState, useEffect, useMemo } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {LogOut, Eye, Trash2, Download, Copy, MessageCircleDashed as MessageCircle, Shield, Baby, X} from 'lucide-react'
import BlocoProtocoloUso from '@/components/BlocoProtocoloUso'
import CalculadoraOleos from '@/components/CalculadoraOleos'
import NotasObservacoes from '@/components/NotasObservacoes'
import { copiarTexto } from '@/utils/clipboard'

const SENHA_ADMIN = 'eusousuprida520'

const NOME_MIX: Record<string, { emoji: string; nomePort: string; descricao: string }> = {
  CALMER:   { emoji: '🌙', nomePort: 'Mix para Serenidade', descricao: 'Sono tranquilo e calma' },
  THINKER:  { emoji: '🧠', nomePort: 'Mix para Foco',       descricao: 'Concentração e aprendizagem' },
  BRAVE:    { emoji: '🦁', nomePort: 'Mix para Coragem',    descricao: 'Autoconfiança e superação' },
  STEADY:   { emoji: '⚖️', nomePort: 'Mix para Equilíbrio', descricao: 'Humor estável e bem-estar' },
  STRONGER: { emoji: '🛡️', nomePort: 'Mix Protetor',        descricao: 'Imunidade e proteção' },
  TAMER:    { emoji: '🌬️', nomePort: 'Mix Digestivo',       descricao: 'Conforto respiratório e digestivo' },
  RESCUER:  { emoji: '💙', nomePort: 'Mix para Conforto',   descricao: 'Alívio de tensão e dores' },
}

// ══════════════════════════════════════════
// Preços oficiais Brasil — Mixes Kids (avulsos) e Touch
// ══════════════════════════════════════════

const PRECOS_MIXES: Record<string, { preco: number; pv: number }> = {
  CALMER: { preco: 150.0, pv: 20 },
  THINKER: { preco: 127.5, pv: 17 },
  BRAVE: { preco: 165.0, pv: 22 },
  STEADY: { preco: 120.0, pv: 16 },
  STRONGER: { preco: 112.5, pv: 15 },
  TAMER: { preco: 112.5, pv: 15 },
  RESCUER: { preco: 120.0, pv: 16 },
}

const PRECOS_TOUCH: Record<string, { preco: number; pv: number; soAdolescente?: boolean }> = {
  'Lavanda Touch': { preco: 120.0, pv: 16 },
  'Vetiver Touch': { preco: 247.5, pv: 33 },
  'Breathe Touch': { preco: 112.5, pv: 15 },
  'On Guard Touch': { preco: 180.0, pv: 24, soAdolescente: true },
  'Adaptiv Touch': { preco: 150.0, pv: 20, soAdolescente: true },
  'Hortelã-Pimenta Touch': { preco: 112.5, pv: 15, soAdolescente: true },
  'Zengest Touch': { preco: 172.5, pv: 23, soAdolescente: true },
}

// Aliases para variações de nome que podem vir do diagnóstico
const ALIAS_TOUCH: Record<string, string> = {
  'Hortelã Touch': 'Hortelã-Pimenta Touch',
  'Hortela Touch': 'Hortelã-Pimenta Touch',
  'Peppermint Touch': 'Hortelã-Pimenta Touch',
  'Lavender Touch': 'Lavanda Touch',
  'ZenGest Touch': 'Zengest Touch',
  'OnGuard Touch': 'On Guard Touch',
}

function resolverNomeTouch(nome: string): string {
  return ALIAS_TOUCH[nome] || nome
}

// Kit Kids Completo: 7 mixes Kids — preço com desconto e PV oficial
const KIT_KIDS_COMPLETO = { preco: 766.25, pv: 89 }
const SOMA_AVULSO_MIXES_KIDS = 907.50 // 150 + 127.5 + 165 + 120 + 112.5 + 112.5 + 120
const ECONOMIA_KIT_KIDS = 141.25 // 907.50 - 766.25

// Difusor Petal 2.0 (com brindes inclusos: Lavanda 5ml + Laranja-selvagem 5ml)
const DIFUSOR_PETAL = { preco: 498.75, pv: 30 }

// Frete fixo Brasil (sempre R$32,00, independente do valor)
const FRETE = 32.0
// Taxa de adesão (cobrada quando PV total < 100)
const TAXA_ADESAO = 48.0
const PV_MINIMO_SEM_TAXA = 100

const NOME_FAIXA: Record<string, string> = {
  BEBE: 'Bebê (0-2 anos)',
  PRE_ESCOLAR: 'Pré-escolar (3-6 anos)',
  CRIANCA: 'Criança (7-12 anos)',
  ADOLESCENTE: 'Adolescente (13-17 anos)',
}

function obterCoresStatus(status: string) {
  const cores: Record<string, { bg: string; text: string }> = {
    'Novo lead': { bg: '#E6F1FB', text: '#185FA5' },
    'Em contato': { bg: '#FAEEDA', text: '#854F0B' },
    'Agendado': { bg: '#F3E8FF', text: '#6B21A8' },
    'Convertido': { bg: '#D8F3DC', text: '#2D6A4F' },
    'Não respondeu': { bg: '#FCEBEB', text: '#A32D2D' },
    'Não qualificado': { bg: '#F3F4F6', text: '#4B5563' },
  }
  return cores[status] || { bg: '#F3F4F6', text: '#6B7280' }
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Parcelamento sempre calculado sobre o total COM frete
// < R$300: à vista | R$300 a R$599,99: 3x | >= R$600: 3x e 6x
function calcularParcelamento(totalComFrete: number) {
  if (totalComFrete >= 600) {
    return { em3x: totalComFrete / 3, em6x: totalComFrete / 6 }
  }
  if (totalComFrete >= 300) {
    return { em3x: totalComFrete / 3, em6x: null as number | null }
  }
  return null
}

interface OpcaoCompra {
  titulo: string
  emoji: string
  observacaoKids: boolean
  destaqueEconomia?: { texto: string; avulso: number; kit: number; economia: number }
  destaqueBrindes?: string[]
  descricao: string
  observacaoTopo?: string
  produtos: { nome: string; tamanho?: string; preco: number; pv: number; brinde?: boolean }[]
  subtotal: number
  totalPV: number
  frete: number
  totalComFrete: number
  taxaIsenta: boolean
  parcelamento: { em3x: number; em6x: number | null } | null
  borda: string
  fundo?: string
  semValores?: boolean // se true não exibe nenhum produto/preço (caso vazio)
  ocultarPrecosIndividuais?: boolean // se true lista só nomes sem preços unitários
}

// ══════════════════════════════════════════
// Geração das 4 opções
// ══════════════════════════════════════════

function gerarOpcao1Completo(nomeKid: string): OpcaoCompra {
  const produtos = [
    { nome: 'Calmer', tamanho: '10ml', preco: 150.0, pv: 20 },
    { nome: 'Thinker', tamanho: '10ml', preco: 127.5, pv: 17 },
    { nome: 'Brave', tamanho: '10ml', preco: 165.0, pv: 22 },
    { nome: 'Steady', tamanho: '10ml', preco: 120.0, pv: 16 },
    { nome: 'Stronger', tamanho: '10ml', preco: 112.5, pv: 15 },
    { nome: 'Tamer', tamanho: '10ml', preco: 112.5, pv: 15 },
    { nome: 'Rescuer', tamanho: '10ml', preco: 120.0, pv: 16 },
  ]
  const subtotal = KIT_KIDS_COMPLETO.preco
  const totalPV = KIT_KIDS_COMPLETO.pv
  const totalComFrete = subtotal + FRETE
  return {
    titulo: 'Kit Kids Completo dōTERRA',
    emoji: '🌿',
    observacaoKids: true,
    ocultarPrecosIndividuais: true,
    destaqueEconomia: {
      texto: `Comprando o kit você economiza R$${fmt(ECONOMIA_KIT_KIDS)} em relação à compra dos 7 óleos avulsos!`,
      avulso: SOMA_AVULSO_MIXES_KIDS,
      kit: KIT_KIDS_COMPLETO.preco,
      economia: ECONOMIA_KIT_KIDS,
    },
    descricao: `7 mixes roll-on: Calmer, Thinker, Brave, Steady, Stronger, Tamer e Rescuer.\nTodos prontos para usar sem precisar diluir. A solução completa para ${nomeKid}.`,
    produtos,
    subtotal,
    totalPV,
    frete: FRETE,
    totalComFrete,
    taxaIsenta: totalPV >= PV_MINIMO_SEM_TAXA,
    parcelamento: calcularParcelamento(totalComFrete),
    borda: '#2D6A4F',
    fundo: '#F0FAF4',
  }
}

function gerarOpcao2Prioritario(mixesIndicados: string[], nomeKid: string): OpcaoCompra {
  // Apenas mixes da linha Kids indicados (máximo 4)
  const mixesTop4 = mixesIndicados.slice(0, 4)
  const produtos = mixesTop4
    .map(m => {
      const info = PRECOS_MIXES[m]
      if (!info) return null
      const nomeExibir = NOME_MIX[m]?.nomePort ? `${NOME_MIX[m].nomePort} (${m.charAt(0) + m.slice(1).toLowerCase()})` : m
      return { nome: nomeExibir, tamanho: '10ml', preco: info.preco, pv: info.pv }
    })
    .filter((p): p is NonNullable<typeof p> => p !== null)

  const subtotal = produtos.reduce((acc, p) => acc + p.preco, 0)
  const totalPV = produtos.reduce((acc, p) => acc + p.pv, 0)
  const totalComFrete = subtotal + FRETE
  return {
    titulo: 'Kit Prioritário Kids',
    emoji: '🌿',
    observacaoKids: true,
    descricao: `Os mixes da linha dōTERRA Kids mais indicados para as necessidades específicas de ${nomeKid} agora.`,
    produtos,
    subtotal,
    totalPV,
    frete: FRETE,
    totalComFrete,
    taxaIsenta: totalPV >= PV_MINIMO_SEM_TAXA,
    parcelamento: calcularParcelamento(totalComFrete),
    borda: '#52B788',
    semValores: produtos.length === 0,
  }
}

function gerarOpcao3Touch(oleosTouchInd: string[], faixaEtaria: string, nomeKid: string): OpcaoCompra {
  const ehAdolescente = faixaEtaria === 'ADOLESCENTE'
  const produtos = oleosTouchInd
    .map(t => {
      const nomeResolvido = resolverNomeTouch(t)
      const info = PRECOS_TOUCH[nomeResolvido]
      if (!info) return null
      // Filtra óleos que são apenas para adolescente quando faixa não é adolescente
      if (info.soAdolescente && !ehAdolescente) return null
      return { nome: nomeResolvido, tamanho: '10ml', preco: info.preco, pv: info.pv }
    })
    .filter((p): p is NonNullable<typeof p> => p !== null)

  const subtotal = produtos.reduce((acc, p) => acc + p.preco, 0)
  const totalPV = produtos.reduce((acc, p) => acc + p.pv, 0)
  const totalComFrete = subtotal + FRETE
  return {
    titulo: 'Kit Touch',
    emoji: '💧',
    observacaoKids: false,
    descricao: `Óleos essenciais em versão touch — diluídos e prontos para usar, sem precisar diluir.\n\n✨ Indicados para ${nomeKid} e também podem ser usados por toda a família: mãe, pai e irmãos.\n\nUm investimento que cuida de todos em casa.`,
    observacaoTopo: `💡 Os óleos touch são diluídos e prontos para usar diretamente na pele. Além de indicados para ${nomeKid} podem ser usados por toda a família.`,
    produtos,
    subtotal,
    totalPV,
    frete: FRETE,
    totalComFrete,
    taxaIsenta: totalPV >= PV_MINIMO_SEM_TAXA,
    parcelamento: calcularParcelamento(totalComFrete),
    borda: '#7AB8E8',
    semValores: produtos.length === 0,
  }
}

function gerarOpcao4Premium(): OpcaoCompra {
  const produtos = [
    { nome: 'Kit Kids Completo (7 mixes)', preco: KIT_KIDS_COMPLETO.preco, pv: KIT_KIDS_COMPLETO.pv },
    { nome: 'Difusor Petal 2.0', preco: DIFUSOR_PETAL.preco, pv: DIFUSOR_PETAL.pv },
    { nome: '🎁 BRINDE: Lavanda 5ml (incluso no Petal 2.0)', preco: 0, pv: 0, brinde: true },
    { nome: '🎁 BRINDE: Laranja-selvagem 5ml (incluso no Petal 2.0)', preco: 0, pv: 0, brinde: true },
  ]
  const subtotal = KIT_KIDS_COMPLETO.preco + DIFUSOR_PETAL.preco
  const totalPV = KIT_KIDS_COMPLETO.pv + DIFUSOR_PETAL.pv
  const totalComFrete = subtotal + FRETE
  return {
    titulo: 'Kit Premium',
    emoji: '👑',
    observacaoKids: true,
    destaqueEconomia: {
      texto: `Comprando o kit você economiza R$${fmt(ECONOMIA_KIT_KIDS)} nos mixes em relação à compra avulsa!`,
      avulso: SOMA_AVULSO_MIXES_KIDS,
      kit: KIT_KIDS_COMPLETO.preco,
      economia: ECONOMIA_KIT_KIDS,
    },
    destaqueBrindes: ['Lavanda 5ml', 'Laranja-selvagem 5ml'],
    descricao: 'O kit mais completo com todos os mixes dōTERRA Kids + difusor Petal com brindes incluídos. Para todas as faixas etárias.',
    produtos,
    subtotal,
    totalPV,
    frete: FRETE,
    totalComFrete,
    taxaIsenta: totalPV >= PV_MINIMO_SEM_TAXA,
    parcelamento: calcularParcelamento(totalComFrete),
    borda: '#B08968',
    fundo: '#FAF6F0',
  }
}

// ══════════════════════════════════════════
// Componente Card de Opção
// ══════════════════════════════════════════

function gerarTextoOpcao(numero: number, opcao: OpcaoCompra): string {
  const totalFinal = opcao.totalComFrete + (opcao.taxaIsenta ? 0 : TAXA_ADESAO)
  const linhas: string[] = []

  linhas.push(`*OPÇÃO ${numero} — ${opcao.titulo}*`)
  linhas.push('')

  if (opcao.observacaoKids) {
    linhas.push('🌿 Linha dōTERRA Kids — indicada para crianças e adolescentes de 0 a 17 anos')
    linhas.push('')
  }

  if (opcao.destaqueEconomia) {
    linhas.push(`💰 ${opcao.destaqueEconomia.texto}`)
    linhas.push(`Avulso: R$${fmt(opcao.destaqueEconomia.avulso)} → Kit: R$${fmt(opcao.destaqueEconomia.kit)}`)
    linhas.push('')
  }

  if (opcao.destaqueBrindes && opcao.destaqueBrindes.length > 0) {
    linhas.push(`🎁 Brindes incluídos: ${opcao.destaqueBrindes.join(' + ')}`)
    linhas.push('')
  }

  // Produtos
  if (opcao.ocultarPrecosIndividuais && opcao.produtos.length > 0) {
    // Opção 1: lista os nomes sem preços
    opcao.produtos
      .filter(p => !p.brinde)
      .forEach(p => linhas.push(`- ${p.nome}`))
    linhas.push('')
  } else if (!opcao.ocultarPrecosIndividuais && opcao.produtos.length > 0) {
    opcao.produtos.forEach(p => {
      if (p.brinde) {
        linhas.push(`${p.nome}`)
      } else {
        linhas.push(`- ${p.nome}${p.tamanho ? ' ' + p.tamanho : ''}: R$${fmt(p.preco)}`)
      }
    })
    if (!opcao.taxaIsenta) {
      linhas.push(`- Taxa de adesão dōTERRA: R$${fmt(TAXA_ADESAO)}`)
    } else {
      linhas.push('- Taxa de adesão dōTERRA: Isenta 🎉')
    }
    linhas.push(`- Frete: R$${fmt(opcao.frete)}`)
    linhas.push('')
  }

  linhas.push('*Investimento:*')
  linhas.push(`- Produtos: R$${fmt(opcao.subtotal)}`)
  if (!opcao.taxaIsenta) {
    linhas.push(`- Taxa de adesão: R$${fmt(TAXA_ADESAO)}`)
  } else {
    linhas.push('- Taxa de adesão: Isenta 🎉')
  }
  linhas.push(`- Frete: R$${fmt(opcao.frete)}`)
  linhas.push(`- *Total: R$${fmt(totalFinal)}*`)

  if (opcao.parcelamento) {
    const p3 = totalFinal / 3
    if (opcao.parcelamento.em6x !== null) {
      const p6 = totalFinal / 6
      linhas.push(`- Parcelado em 3x de R$${fmt(p3)} ou 6x de R$${fmt(p6)} no cartão`)
    } else {
      linhas.push(`- Parcelado em 3x de R$${fmt(p3)} no cartão`)
    }
  }
  linhas.push('- Ou à vista no Pix/boleto')
  linhas.push('')

  if (opcao.taxaIsenta) {
    linhas.push('*Sobre a taxa de adesão:*')
    linhas.push('🎉 Taxa de adesão isenta!')
    linhas.push('Compra acima de 100 PV.')
    linhas.push('Benefício: 25% de desconto em futuras compras durante o ano.')
  } else {
    linhas.push(`*Sobre a taxa de adesão de R$${fmt(TAXA_ADESAO)}:*`)
    linhas.push('O kit de adesão dōTERRA inclui uma seleção de óleos essenciais e garante 25% de desconto em todas as compras durante o ano.')
    linhas.push('A taxa é isenta em compras acima de 100 PV.')
  }

  return linhas.join('\n')
}

function CardOpcao({ numero, opcao }: { numero: number; opcao: OpcaoCompra }) {
  const [copiado, setCopiado] = useState(false)
  const copiarOpcao = async () => {
    try {
      await copiarTexto(gerarTextoOpcao(numero, opcao))
      setCopiado(true)
      toast.success('Opção copiada!')
      setTimeout(() => setCopiado(false), 2000)
    } catch { toast.error('Erro ao copiar') }
  }

  if (opcao.semValores) {
    return (
      <div className="border-2 border-dashed rounded-xl p-4" style={{ borderColor: '#EDE0D4' }}>
        <div className="text-sm font-semibold text-[#2D6A4F] mb-1">
          OPÇÃO {numero} — {opcao.emoji} {opcao.titulo}
        </div>
        <div className="text-xs text-[#5F5E5A] italic">
          Nenhum produto correspondente foi indicado pelo diagnóstico para esta opção.
        </div>
      </div>
    )
  }

  return (
    <div
      className="border-2 rounded-xl overflow-hidden"
      style={{ borderColor: opcao.borda, backgroundColor: opcao.fundo || '#FFFFFF' }}
    >
      {/* Cabeçalho */}
      <div className="px-4 py-3 border-b" style={{ borderColor: opcao.borda, backgroundColor: opcao.borda + '15' }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: opcao.borda }}>
              Opção {numero}
            </div>
            <h4 className="text-base font-cormorant font-semibold text-[#2C2C2A]">
              {opcao.emoji} {opcao.titulo}
            </h4>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Observação linha Kids */}
        {opcao.observacaoKids && (
          <div className="bg-[#D8F3DC] border border-[#A8DFC4] rounded-lg px-3 py-2 text-xs text-[#2D6A4F]">
            🌿 <strong>Linha dōTERRA Kids</strong> — indicada para crianças e adolescentes de 0 a 17 anos
          </div>
        )}

        {/* Observação topo (Kit Touch) */}
        {opcao.observacaoTopo && (
          <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg px-3 py-2 text-xs text-[#854F0B]">
            {opcao.observacaoTopo}
          </div>
        )}

        {/* Destaque de economia */}
        {opcao.destaqueEconomia && (
          <div className="bg-[#D8F3DC] border-2 border-[#52B788] rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-lg">💰</span>
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#2D6A4F] mb-1">{opcao.destaqueEconomia.texto}</div>
                <div className="text-xs text-[#2D6A4F] space-y-0.5">
                  <div>Avulso: R${fmt(opcao.destaqueEconomia.avulso)}</div>
                  <div>Kit: R${fmt(opcao.destaqueEconomia.kit)}</div>
                  <div className="font-semibold">Economia: R${fmt(opcao.destaqueEconomia.economia)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Destaque dos brindes */}
        {opcao.destaqueBrindes && opcao.destaqueBrindes.length > 0 && (
          <div className="bg-[#D8F3DC] border-2 border-[#52B788] rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-lg">🎁</span>
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#2D6A4F] mb-1">Brindes incluídos:</div>
                <div className="text-xs text-[#2D6A4F]">{opcao.destaqueBrindes.join(' + ')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Descrição */}
        <div className="text-sm text-[#5F5E5A] whitespace-pre-line">{opcao.descricao}</div>

        {/* Lista de produtos — Opção 1 oculta preços individuais, demais exibem */}
        <div className="bg-[#F8F4EF] rounded-lg p-3 space-y-1.5">
          {opcao.produtos.map((p, i) => (
            <div key={i} className="flex justify-between items-center text-xs">
              <span className={p.brinde ? 'text-[#2D6A4F] font-medium' : 'text-[#2C2C2A]'}>
                {p.nome}{p.tamanho ? ` ${p.tamanho}` : ''}
              </span>
              {!p.brinde && !opcao.ocultarPrecosIndividuais && (
                <span className="text-[#5F5E5A] whitespace-nowrap">
                  R${fmt(p.preco)} — {p.pv} PV
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Investimento */}
        <div className="bg-white border border-[#EDE0D4] rounded-lg p-3 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-[#5F5E5A]">Subtotal produtos:</span>
            <span className="text-[#2C2C2A] font-medium">R${fmt(opcao.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#5F5E5A]">PV total:</span>
            <span className="text-[#2C2C2A] font-medium">{opcao.totalPV} PV</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#5F5E5A]">Frete:</span>
            <span className="text-[#2C2C2A] font-medium">R${fmt(opcao.frete)}</span>
          </div>
          {!opcao.taxaIsenta && (
            <div className="flex justify-between">
              <span className="text-[#5F5E5A]">Taxa de adesão:</span>
              <span className="text-[#2C2C2A] font-medium">R${fmt(TAXA_ADESAO)}</span>
            </div>
          )}
          <div className="border-t border-[#EDE0D4] pt-1.5 mt-1.5 flex justify-between">
            <span className="text-[#2C2C2A] font-semibold">Total:</span>
            <span className="text-[#2D6A4F] font-bold text-base">
              R${fmt(opcao.totalComFrete + (opcao.taxaIsenta ? 0 : TAXA_ADESAO))}
            </span>
          </div>
        </div>

        {/* Caixa de taxa de adesão */}
        {opcao.taxaIsenta ? (
          <div className="bg-[#D8F3DC] border-2 border-[#52B788] rounded-lg p-3">
            <div className="text-sm font-semibold text-[#2D6A4F]">🎉 Taxa de adesão isenta!</div>
            <div className="text-xs text-[#2D6A4F] mt-1">25% de desconto em futuras compras durante o ano.</div>
          </div>
        ) : (
          <div className="bg-[#FEF3C7] border-2 border-[#F59E0B] rounded-lg p-3">
            <div className="text-sm font-semibold text-[#854F0B]">⚠️ Taxa de adesão: R$48,00</div>
            <div className="text-xs text-[#854F0B] mt-1">Inclui seleção de óleos e garante 25% de desconto durante o ano.</div>
          </div>
        )}

        {/* Parcelamento */}
        {opcao.parcelamento ? (
          <div className="bg-white border border-[#A8DFC4] rounded-lg p-3 text-xs space-y-0.5">
            <div className="font-semibold text-[#2D6A4F] mb-1">Formas de pagamento:</div>
            <div className="text-[#2C2C2A]">• À vista no Pix / boleto: <strong>R${fmt(opcao.totalComFrete + (opcao.taxaIsenta ? 0 : TAXA_ADESAO))}</strong></div>
            <div className="text-[#2C2C2A]">• Parcelado em <strong>3x de R${fmt(opcao.parcelamento.em3x + (opcao.taxaIsenta ? 0 : TAXA_ADESAO / 3))}</strong> no cartão</div>
            {opcao.parcelamento.em6x !== null && (
              <div className="text-[#2C2C2A]">• Parcelado em <strong>6x de R${fmt(opcao.parcelamento.em6x + (opcao.taxaIsenta ? 0 : TAXA_ADESAO / 6))}</strong> no cartão</div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-[#A8DFC4] rounded-lg p-3 text-xs">
            <span className="text-[#2C2C2A]">À vista no Pix / boleto / cartão</span>
          </div>
        )}

        {/* Botão copiar opção */}
        <button
          onClick={copiarOpcao}
          className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${copiado ? 'bg-[#D8F3DC] text-[#2D6A4F]' : 'bg-[#2D6A4F] text-white hover:bg-[#245A42]'}`}
        >
          <Copy className="w-4 h-4" />
          {copiado ? 'Copiado!' : 'Copiar opção'}
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════
// Modal de Lead Kids
// ══════════════════════════════════════════

function ModalLeadKids({ lead, onFechar, onAtualizar }: { lead: any; onFechar: () => void; onAtualizar: () => void }) {
  const [status, setStatus] = useState(lead.status || 'Novo lead')
  const [copiado, setCopiado] = useState<Record<string, boolean>>({})

  const nomeMaeDisplay = (lead.nomeMae || '').split(' ')[0]
  const nomeKidDisplay = (lead.nomeKid || '').split(' ')[0]

  const mixesIndicados: string[] = lead.mixesIndicados || []
  const oleosTouch: string[] = lead.oleosTouch || []
  const prioridades: string[] = lead.prioridades || []
  const faixaEtaria: string = lead.faixaEtaria || ''
  const conheceOleos: string = lead.conheceOleos || ''
  const cadastradaDoterra: string = lead.cadastradaDoterra || ''

  // Badge de qualificação
  const badgeCadastro = (() => {
    if (!cadastradaDoterra) return null
    if (cadastradaDoterra === 'Não sou cadastrada') return { cor: 'bg-[#D8F3DC] text-[#2D6A4F] border-[#A8DFC4]', label: '🟢 Lead livre' }
    if (cadastradaDoterra === 'Sou cadastrada mas não estou ativa') return { cor: 'bg-[#FEF3C7] text-[#854F0B] border-[#FCD34D]', label: '🟡 Verificar vínculo' }
    return { cor: 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]', label: '🔴 Já cadastrada' }
  })()

  const orientacaoAbordagem = (() => {
    if (!cadastradaDoterra) return null
    if (cadastradaDoterra === 'Sou cadastrada mas não estou ativa') {
      return 'Atenção! Verificar o vínculo com outra consultora antes de prosseguir com a venda.'
    }
    if (cadastradaDoterra === 'Sou cadastrada e estou ativa') {
      return 'Já é cliente dōTERRA. Verificar se pode atender ou se há vínculo com outra consultora. Abordagem diferente necessária.'
    }
    // Não cadastrada — variar conforme conheceOleos
    if (conheceOleos === 'Nunca ouvi falar') return 'Tom educativo. Comece explicando o que são os óleos essenciais antes de apresentar os produtos.'
    if (conheceOleos === 'Já ouvi falar mas nunca usei') return `Já tem curiosidade. Foque nos benefícios específicos para ${nomeKidDisplay} sem precisar explicar o básico.`
    if (conheceOleos === 'Já usei outros óleos essenciais') return 'Já conhece óleos. Destaque os diferenciais da dōTERRA e da linha Kids especificamente.'
    if (conheceOleos === 'Já usei óleos dōTERRA') return 'Lead quente! Já conhece a marca. Vá direto para os mixes indicados e valores.'
    return null
  })()

  // 4 opções de compra (memorizadas)
  const opcao1 = useMemo(() => gerarOpcao1Completo(nomeKidDisplay), [nomeKidDisplay])
  const opcao2 = useMemo(() => gerarOpcao2Prioritario(mixesIndicados, nomeKidDisplay), [mixesIndicados, nomeKidDisplay])
  const opcao3 = useMemo(() => gerarOpcao3Touch(oleosTouch, faixaEtaria, nomeKidDisplay), [oleosTouch, faixaEtaria, nomeKidDisplay])
  const opcao4 = useMemo(() => gerarOpcao4Premium(), [])

  const atualizarStatus = async (novoStatus: string) => {
    setStatus(novoStatus)
    try {
      await lumi.entities.leads_kids.update(lead._id, { status: novoStatus, updatedAt: new Date().toISOString() })
      toast.success('Status atualizado!')
      onAtualizar()
    } catch { toast.error('Erro ao atualizar status') }
  }

  const copiar = async (texto: string, chave: string) => {
    try {
      await copiarTexto(texto)
      setCopiado(p => ({ ...p, [chave]: true }))
      toast.success('Copiado!')
      setTimeout(() => setCopiado(p => ({ ...p, [chave]: false })), 2000)
    } catch { toast.error('Erro ao copiar') }
  }

  // ══════════════════════════════════════════
  // Script de vendas — 2 mensagens
  // ══════════════════════════════════════════

  const prioridadesTexto = prioridades.length > 0 ? prioridades.join(', ') : 'as questões mais relevantes'
  const mixesTexto = mixesIndicados.map(m => `${NOME_MIX[m]?.emoji || ''} *${NOME_MIX[m]?.nomePort || m}*\n${NOME_MIX[m]?.descricao || ''}`).join('\n\n')

  const script1 = `Olá, ${nomeMaeDisplay}! 🌿

Recebi o diagnóstico de saúde e bem-estar de ${nomeKidDisplay} e já analisei as respostas com cuidado.

Com base em tudo que você respondeu, esses são os óleos essenciais indicados para ${nomeKidDisplay} agora:

${mixesTexto || '(nenhum mix selecionado)'}

São todos roll-on prontos para usar sem precisar diluir e 100% seguros para crianças.

Vi que ${nomeKidDisplay} precisa principalmente de: ${prioridadesTexto}. Isso me diz muito sobre o que ela precisa agora.`

  const script2 = `Vamos agendar um momento para eu te apresentar como você pode iniciar com ${nomeKidDisplay} e tirar todas as suas dúvidas. Qual o melhor horário para você? 📅`

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#2D6A4F] text-white px-6 py-4 flex justify-between items-center z-10 rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-cormorant font-semibold">Lead Kids — {lead.nomeKid}</h2>
            <p className="text-[#A8DFC4] text-sm mt-0.5">{lead.nomeMae} · {NOME_FAIXA[lead.faixaEtaria] || lead.faixaEtaria}</p>
          </div>
          <button onClick={onFechar} className="text-white hover:text-gray-200 p-1">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* SEÇÃO 1 — Dados básicos */}
          <section>
            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-[#EDE0D4]">
              1. Dados Básicos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'Mãe/Responsável', valor: lead.nomeMae },
                { label: 'WhatsApp', valor: lead.whatsapp },
                { label: 'Nome da criança', valor: lead.nomeKid },
                { label: 'Idade', valor: lead.idadeKid },
                { label: 'Faixa etária', valor: NOME_FAIXA[lead.faixaEtaria] || lead.faixaEtaria },
                { label: 'País', valor: lead.pais },
                { label: 'Data', valor: lead.data_hora ? new Date(lead.data_hora).toLocaleString('pt-BR') : '—' },
                { label: 'Consultora', valor: lead.consultora || 'geral' },
              ].map(({ label, valor }) => (
                <div key={label}>
                  <div className="text-xs text-[#5F5E5A] mb-1 font-medium">{label}</div>
                  <div className="text-[#2C2C2A] text-sm">{valor || '—'}</div>
                </div>
              ))}
            </div>

            {/* Badges de qualificação — apenas informação, sem orientação aqui */}
            {(conheceOleos || cadastradaDoterra) && (
              <div className="mt-5 space-y-2">
                {conheceOleos && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#5F5E5A] font-medium">Conhece óleos:</span>
                    <span className="px-3 py-1 bg-[#F8F4EF] border border-[#EDE0D4] rounded-full text-xs text-[#2C2C2A]">{conheceOleos}</span>
                  </div>
                )}
                {badgeCadastro && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#5F5E5A] font-medium">Status dōTERRA:</span>
                    <span className={`px-3 py-1 border rounded-full text-xs font-semibold ${badgeCadastro.cor}`}>{badgeCadastro.label}</span>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* SEÇÃO 2 — Respostas do diagnóstico */}
          {lead.respostas && Object.keys(lead.respostas).length > 0 && (
            <section>
              <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-[#EDE0D4]">
                2. Respostas do Diagnóstico
              </h3>
              <div className="space-y-3">
                {Object.entries(lead.respostas).map(([tela, resp]) => (
                  <div key={tela} className="bg-[#F8F4EF] rounded-xl px-4 py-3">
                    <div className="text-xs text-[#B08968] font-medium uppercase tracking-wide mb-1">{tela}</div>
                    <div className="text-sm text-[#2C2C2A]">{Array.isArray(resp) ? resp.join(', ') : String(resp)}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* SEÇÃO 3 — Orientação de abordagem */}
          {(conheceOleos || cadastradaDoterra) && (
            <section>
              <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-[#EDE0D4]">
                3. 💡 Orientação de Abordagem
              </h3>
              {orientacaoAbordagem && (
                <div className={`rounded-xl px-4 py-3 text-sm border ${
                  cadastradaDoterra === 'Não sou cadastrada'
                    ? 'bg-[#D8F3DC] border-[#A8DFC4] text-[#2D6A4F]'
                    : cadastradaDoterra === 'Sou cadastrada mas não estou ativa'
                    ? 'bg-[#FEF3C7] border-[#FCD34D] text-[#854F0B]'
                    : 'bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B]'
                }`}>
                  <span className="font-semibold">💡 </span>{orientacaoAbordagem}
                </div>
              )}
            </section>
          )}

          {/* SEÇÃO 4 — Demandas e Prioridades */}
          {((lead.demandaEspecial || []).length > 0 || prioridades.length > 0) && (
            <section>
              <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3 pb-2 border-b border-[#EDE0D4]">
                4. Prioridades
              </h3>
              {(lead.demandaEspecial || []).length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-[#5F5E5A] font-medium mb-2">Demandas especiais:</div>
                  <div className="flex flex-wrap gap-2">
                    {(lead.demandaEspecial as string[]).map((d: string) => (
                      <span key={d} className="px-3 py-1 bg-[#FEF3C7] text-[#92400E] rounded-full text-sm">{d}</span>
                    ))}
                  </div>
                </div>
              )}
              {prioridades.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {prioridades.map((p, i) => (
                    <span key={i} className="flex items-center gap-1.5 px-3 py-1 bg-[#D8F3DC] text-[#2D6A4F] rounded-full text-sm font-medium">
                      ✅ {p}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* SEÇÃO 5 — Notas e Observações */}
          <NotasObservacoes
            leadId={lead._id}
            colecao="leads_kids"
            notasIniciais={lead.notasObservacoes || []}
            titulo="5. 📝 Notas e Observações"
          />

          {/* SEÇÃO 6 — Script de vendas */}
          <section>
            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-[#EDE0D4]">
              6. 💬 Script de Vendas
            </h3>
            <div className="space-y-4">
              {[
                { titulo: 'Mensagem 1 — Óleos indicados', texto: script1, chave: 's1' },
                { titulo: 'Mensagem 2 — Agendamento', texto: script2, chave: 's2' },
              ].map(({ titulo, texto, chave }) => (
                <div key={chave}>
                  <div className="text-sm font-medium text-[#5F5E5A] mb-2">{titulo}</div>
                  <div className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3 text-sm text-[#2C2C2A] whitespace-pre-wrap mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                    {texto}
                  </div>
                  <button
                    onClick={() => copiar(texto, chave)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${copiado[chave] ? 'bg-[#D8F3DC] text-[#2D6A4F]' : 'bg-[#2D6A4F] text-white hover:bg-[#245A42]'}`}
                  >
                    <Copy className="w-4 h-4" />
                    {copiado[chave] ? 'Copiado!' : 'Copiar mensagem'}
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* SEÇÃO 7 — Mixes indicados (sem valores) */}
          {mixesIndicados.length > 0 && (
            <section>
              <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-[#EDE0D4]">
                7. 🌿 Mixes Indicados
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {mixesIndicados.map(mix => (
                  <div key={mix} className="flex items-start gap-3 p-4 bg-[#F8F4EF] rounded-xl border border-[#EDE0D4]">
                    <span className="text-2xl">{NOME_MIX[mix]?.emoji || '🌿'}</span>
                    <div>
                      <div className="font-semibold text-[#2D6A4F] text-sm">{NOME_MIX[mix]?.nomePort || mix}</div>
                      <div className="text-xs text-[#5F5E5A] mt-0.5">{NOME_MIX[mix]?.descricao}</div>
                      <div className="text-xs text-[#B08968] mt-1">Referência interna: dōTERRA {mix.charAt(0) + mix.slice(1).toLowerCase()}</div>
                      <div className="text-xs text-[#B08968]">Aplicação: roll-on direto na pele</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* SEÇÃO 8 — Óleos touch (sem valores) */}
          {oleosTouch.length > 0 && (
            <section>
              <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3 pb-2 border-b border-[#EDE0D4]">
                8. 💧 Óleos Touch Indicados
              </h3>
              <div className="flex flex-wrap gap-2">
                {oleosTouch.map(t => (
                  <span key={t} className="px-3 py-1.5 bg-[#D8F3DC] text-[#2D6A4F] rounded-full text-sm font-medium">
                    {resolverNomeTouch(t)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* SEÇÃO 9 — Opções de compra (4 opções) */}
          <section>
            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-4 pb-2 border-b border-[#EDE0D4]">
              9. 🛒 Opções de Compra
            </h3>
            <div className="space-y-5">
              <CardOpcao numero={1} opcao={opcao1} />
              <CardOpcao numero={2} opcao={opcao2} />
              <CardOpcao numero={3} opcao={opcao3} />
              <CardOpcao numero={4} opcao={opcao4} />
            </div>
          </section>

          {/* SEÇÃO 10 — Calculadora de Óleos */}
          <section>
            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-2 pb-2 border-b border-[#EDE0D4]">
              10. 🧮 Calculadora de Óleos
            </h3>
            <p className="text-xs text-[#5F5E5A] italic mb-4">
              Calcule combinações personalizadas com qualquer produto do catálogo dōTERRA Brasil.
            </p>
            <CalculadoraOleos />
          </section>

          {/* SEÇÃO 11 — Protocolo de Uso (pós-venda) */}
          <section>
            <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-2 pb-2 border-b border-[#EDE0D4]">
              11. 🌿 Protocolo de Uso
            </h3>
            <p className="text-xs text-[#5F5E5A] italic mb-4">
              Recurso pós-venda para enviar à mãe DEPOIS que ela adquirir os óleos. Orienta locais de aplicação, rotina diária e regras de segurança por faixa etária.
            </p>
            <BlocoProtocoloUso
              nomeCrianca={nomeKidDisplay}
              faixaEtaria={faixaEtaria as any}
              mixesIndicados={mixesIndicados}
              oleosTouchInd={oleosTouch}
              demandasEspeciais={lead.demandaEspecial || []}
            />
          </section>

          {/* Status e ações */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-[#EDE0D4]">
            <div className="relative flex-1 min-w-[200px]">
              <select
                value={status}
                onChange={e => atualizarStatus(e.target.value)}
                className="w-full appearance-none px-4 py-3 pr-10 rounded-xl text-sm font-medium cursor-pointer focus:outline-none"
                style={{ backgroundColor: obterCoresStatus(status).bg, color: obterCoresStatus(status).text }}
              >
                <option value="Novo lead">Novo lead</option>
                <option value="Em contato">Em contato</option>
                <option value="Agendado">Agendado</option>
                <option value="Convertido">Convertido</option>
                <option value="Não respondeu">Não respondeu</option>
                <option value="Não qualificado">Não qualificado</option>
              </select>
            </div>
            <a
              href={`https://wa.me/${(lead.whatsapp || '').replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-[#1ea34e] transition-all"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════
// Painel Admin Kids
// ══════════════════════════════════════════

export default function AdminKids() {
  const [autenticado, setAutenticado] = useState(false)
  const [senha, setSenha] = useState('')
  const [leads, setLeads] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [busca, setBusca] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('todos')
  const [filtroFaixa, setFiltroFaixa] = useState('todos')
  const [filtroConsultora, setFiltroConsultora] = useState('todos')
  const [leadSelecionado, setLeadSelecionado] = useState<any>(null)

  useEffect(() => {
    const s = localStorage.getItem('adminkids_sessao')
    if (s === 'ok') setAutenticado(true)
  }, [])

  useEffect(() => {
    if (autenticado) carregarLeads()
  }, [autenticado])

  const carregarLeads = async () => {
    setLoading(true)
    try {
      const resp = await lumi.entities.leads_kids.list({ sort: { data_hora: -1 }, limit: 500 })
      setLeads(resp?.list ?? [])
    } catch (err) {
      console.error('[admin-kids] erro ao carregar', err)
      toast.error('Erro ao carregar leads kids')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (senha === SENHA_ADMIN) {
      setAutenticado(true)
      localStorage.setItem('adminkids_sessao', 'ok')
      toast.success('Acesso liberado!')
    } else {
      toast.error('Senha incorreta')
    }
  }

  const excluirLead = async (id: string) => {
    if (!confirm('Excluir este lead kids?')) return
    try {
      await lumi.entities.leads_kids.delete(id)
      toast.success('Lead excluído!')
      carregarLeads()
      setLeadSelecionado(null)
    } catch { toast.error('Erro ao excluir') }
  }

  const atualizarStatus = async (id: string, status: string) => {
    try {
      await lumi.entities.leads_kids.update(id, { status, updatedAt: new Date().toISOString() })
      toast.success('Status atualizado!')
      carregarLeads()
    } catch { toast.error('Erro ao atualizar status') }
  }

  const exportarCSV = () => {
    const headers = ['Data', 'Mãe/Responsável', 'WhatsApp', 'Criança', 'Idade', 'Faixa', 'País', 'Consultora', 'Mixes', 'Status']
    const rows = leadsFiltrados.map(l => [
      l.data_hora ? new Date(l.data_hora).toLocaleString('pt-BR') : '',
      l.nomeMae, l.whatsapp, l.nomeKid, l.idadeKid,
      NOME_FAIXA[l.faixaEtaria] || l.faixaEtaria,
      l.pais, l.consultora || 'geral',
      (l.mixesIndicados || []).join('; '), l.status,
    ])
    const csv = 'data:text/csv;charset=utf-8,' + [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n')
    const a = document.createElement('a')
    a.setAttribute('href', encodeURI(csv))
    a.setAttribute('download', `leads_kids_${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const consultorasUnicas = useMemo(() => [...new Set(leads.map(l => l.consultora || 'geral'))].sort(), [leads])

  const leadsFiltrados = useMemo(() => leads.filter(l => {
    const ms = filtroStatus === 'todos' || l.status === filtroStatus
    const mf = filtroFaixa === 'todos' || l.faixaEtaria === filtroFaixa
    const mc = filtroConsultora === 'todos' || (l.consultora || 'geral') === filtroConsultora
    const mb = !busca || (l.nomeMae || '').toLowerCase().includes(busca.toLowerCase()) || (l.nomeKid || '').toLowerCase().includes(busca.toLowerCase()) || (l.whatsapp || '').includes(busca)
    return ms && mf && mc && mb
  }), [leads, filtroStatus, filtroFaixa, filtroConsultora, busca])

  const stats = useMemo(() => ({
    total: leads.length,
    novo: leads.filter(l => l.status === 'Novo lead').length,
    contato: leads.filter(l => l.status === 'Em contato').length,
    convertido: leads.filter(l => l.status === 'Convertido').length,
    naoRespondeu: leads.filter(l => l.status === 'Não respondeu').length,
    bebe: leads.filter(l => l.faixaEtaria === 'BEBE').length,
    preEscolar: leads.filter(l => l.faixaEtaria === 'PRE_ESCOLAR').length,
    crianca: leads.filter(l => l.faixaEtaria === 'CRIANCA').length,
    adolescente: leads.filter(l => l.faixaEtaria === 'ADOLESCENTE').length,
  }), [leads])

  if (!autenticado) {
    return (
      <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-lg">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-[#D8F3DC] rounded-full flex items-center justify-center mx-auto mb-4">
              <Baby className="w-8 h-8 text-[#2D6A4F]" />
            </div>
            <h1 className="text-3xl font-cormorant font-semibold text-[#2C2C2A]">Painel Kids</h1>
            <p className="text-sm text-[#5F5E5A] mt-2">Diagnóstico de Saúde e Emoções para Crianças</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#5F5E5A] mb-2">Senha de acesso</label>
              <input type="password" value={senha} onChange={e => setSenha(e.target.value)}
                className="w-full px-4 py-3 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none"
                placeholder="Digite a senha" />
            </div>
            <button type="submit" className="w-full bg-[#2D6A4F] text-white py-3 rounded-full font-medium hover:bg-[#245A42] transition-all">
              Entrar
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Header */}
      <div className="bg-[#2D6A4F] text-white px-6 py-5">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-2xl font-cormorant font-semibold flex items-center gap-2">
              <Baby className="w-6 h-6" />
              Painel Administrativo — Diagnóstico Kids
            </h1>
            <p className="text-[#A8DFC4] text-sm mt-0.5">Diagnóstico de Saúde e Emoções para Crianças</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs bg-white/20 px-3 py-1 rounded-full">
              <Shield className="w-3.5 h-3.5" /> Administrador(a)
            </span>
            <button
              onClick={() => { setAutenticado(false); localStorage.removeItem('adminkids_sessao') }}
              className="flex items-center gap-2 bg-white text-[#2D6A4F] px-4 py-2 rounded-full hover:bg-opacity-90 text-sm font-medium"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Métricas */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 mb-8">
          {[
            { label: 'Total', valor: stats.total, cor: 'text-[#2C2C2A]' },
            { label: 'Novo lead', valor: stats.novo, cor: 'text-blue-600' },
            { label: 'Em contato', valor: stats.contato, cor: 'text-yellow-600' },
            { label: 'Convertido', valor: stats.convertido, cor: 'text-[#2D6A4F]' },
            { label: 'Não respondeu', valor: stats.naoRespondeu, cor: 'text-red-600' },
            { label: 'Bebê', valor: stats.bebe, cor: 'text-pink-600' },
            { label: 'Pré-escolar', valor: stats.preEscolar, cor: 'text-orange-600' },
            { label: 'Criança', valor: stats.crianca, cor: 'text-indigo-600' },
            { label: 'Adolescente', valor: stats.adolescente, cor: 'text-purple-600' },
          ].map(m => (
            <div key={m.label} className="bg-white p-4 rounded-xl shadow-md text-center">
              <div className={`text-2xl font-bold mb-1 ${m.cor}`}>{m.valor}</div>
              <div className="text-xs text-[#5F5E5A]">{m.label}</div>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div className="bg-white p-5 rounded-xl shadow-md mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs text-[#5F5E5A] mb-1">Status</label>
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:outline-none text-sm">
                <option value="todos">Todos</option>
                <option value="Novo lead">Novo lead</option>
                <option value="Em contato">Em contato</option>
                <option value="Agendado">Agendado</option>
                <option value="Convertido">Convertido</option>
                <option value="Não respondeu">Não respondeu</option>
                <option value="Não qualificado">Não qualificado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#5F5E5A] mb-1">Faixa Etária</label>
              <select value={filtroFaixa} onChange={e => setFiltroFaixa(e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:outline-none text-sm">
                <option value="todos">Todas</option>
                <option value="BEBE">Bebê</option>
                <option value="PRE_ESCOLAR">Pré-escolar</option>
                <option value="CRIANCA">Criança</option>
                <option value="ADOLESCENTE">Adolescente</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#5F5E5A] mb-1">Consultora</label>
              <select value={filtroConsultora} onChange={e => setFiltroConsultora(e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:outline-none text-sm">
                <option value="todos">Todas</option>
                {consultorasUnicas.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[#5F5E5A] mb-1">Buscar</label>
              <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
                placeholder="Nome ou WhatsApp"
                className="w-full px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:outline-none text-sm" />
            </div>
            <div className="flex items-end">
              <button onClick={exportarCSV}
                className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white px-4 py-2 rounded-xl hover:bg-[#245A42] transition-all text-sm">
                <Download className="w-4 h-4" /> CSV
              </button>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#5F5E5A]">Carregando...</div>
          ) : leadsFiltrados.length === 0 ? (
            <div className="p-8 text-center text-[#5F5E5A]">Nenhum lead kids encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8F4EF]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Mãe/Responsável</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">WhatsApp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Criança</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Faixa</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Mixes</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Consultora</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[#2C2C2A]">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EDE0D4]">
                  {leadsFiltrados.map(lead => (
                    <tr key={lead._id} className="hover:bg-[#F8F4EF]">
                      <td className="px-4 py-3 text-xs text-[#5F5E5A] whitespace-nowrap">
                        {lead.data_hora ? new Date(lead.data_hora).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-[#2C2C2A] font-medium">{lead.nomeMae}</td>
                      <td className="px-4 py-3 text-sm">
                        <a href={`https://wa.me/${(lead.whatsapp || '').replace(/\D/g, '')}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[#2D6A4F] hover:underline text-xs">{lead.whatsapp}</a>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#2C2C2A]">
                        <div>{lead.nomeKid}</div>
                        <div className="text-xs text-[#5F5E5A]">{lead.idadeKid}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className={`px-2 py-1 rounded-full font-medium ${
                          lead.faixaEtaria === 'BEBE' ? 'bg-pink-100 text-pink-700'
                          : lead.faixaEtaria === 'PRE_ESCOLAR' ? 'bg-orange-100 text-orange-700'
                          : lead.faixaEtaria === 'CRIANCA' ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-purple-100 text-purple-700'
                        }`}>
                          {lead.faixaEtaria === 'BEBE' ? '👶 Bebê' : lead.faixaEtaria === 'PRE_ESCOLAR' ? '🧒 Pré-escolar' : lead.faixaEtaria === 'CRIANCA' ? '👦 Criança' : '🧑 Adolescente'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#5F5E5A]">
                        {(lead.mixesIndicados || []).length} mixes
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded-full">{lead.consultora || 'geral'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="relative inline-block">
                          <select
                            value={lead.status || 'Novo lead'}
                            onChange={e => atualizarStatus(lead._id, e.target.value)}
                            className="appearance-none px-3 py-1.5 pr-6 rounded-full text-xs font-medium cursor-pointer focus:outline-none"
                            style={{ backgroundColor: obterCoresStatus(lead.status).bg, color: obterCoresStatus(lead.status).text }}
                          >
                            <option value="Novo lead">Novo lead</option>
                            <option value="Em contato">Em contato</option>
                            <option value="Agendado">Agendado</option>
                            <option value="Convertido">Convertido</option>
                            <option value="Não respondeu">Não respondeu</option>
                            <option value="Não qualificado">Não qualificado</option>
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setLeadSelecionado(lead)}
                            className="p-2 text-[#2D6A4F] hover:bg-[#D8F3DC] rounded-lg transition-colors" title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => excluirLead(lead._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {leadSelecionado && (
        <ModalLeadKids
          lead={leadSelecionado}
          onFechar={() => setLeadSelecionado(null)}
          onAtualizar={carregarLeads}
        />
      )}
    </div>
  )
}
